import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { activitiesService } from '../services/activities';
import { coursesService } from '../services/courses';
import { useSectors } from './useSectors';
import { dataStructure } from '../utils/data-structure';
import { setActivities } from '../state/activities';
import { productSubscriptionsService } from '../services/productSubscriptions';

export const useCourses = (maxResults, discipline) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isErrored, setIsErrored] = useState(false);
  const [myCourses, setMyCourses] = useState(null);
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage } = useIntl();

  const { studentId, schoolId, activities, learningActivities } = useSelector(state => {
    const profile = state.profile ?? {};
    return {
      studentId: profile.studentId,
      schoolId: profile.schoolId,
      gradeId: profile.gradeId,
      activities: state.activities.courses,
      learningActivities: state.activities.learningActivities
    };
  });
  const sectors = useSectors();

  const setMyCoursesByActivities = useCallback(
    async (activities, learningActivities) => {
      try {
        // Combine activities and learningActivities if both exist, and extract unique by externalId
        const combinedActivities = [
          ...(activities || []),
          ...(learningActivities || []).map(l => {
            const { contentReference, ...rest } = l;
            return {
              ...rest,
              externalId: l.externalId || contentReference
            };
          })
        ];
        const allActivities = Array.from(new Map(combinedActivities.map(a => [a.externalId, a])).values());
        const externalContextId = allActivities.map(activity => activity.externalId);

        let productSubscriptions = [];
        try {
          productSubscriptions = await productSubscriptionsService.loadProductsSubscriptions(studentId, true);
        } catch {}

        let externalCoursesList = [
          { name: 'פלתורה', domainProductID: 'C916655E-B7F8-4D15-A7B6-ADD95F4EFA23', externalContextId: '1' },
          {
            name: 'Magnilearn',
            domainProductID: formatMessage({ id: 'Magnilearn_externalContextId' }),
            externalContextId: formatMessage({ id: 'Magnilearn_externalContextId' })
          }
          /*{name:'fullproof', productId: 'CF4466EC-CAC9-4418-BAD1-6A48923E3149', nairobiId: '48BAAF35'}//replace with fullproof data*/
        ];

        externalCoursesList.forEach(item => {
          if (productSubscriptions.find(x => x.domainProductID.toUpperCase() === item.domainProductID.toUpperCase())) {
            externalContextId.push(item.externalContextId);
          }
        });

        const searchResults =
          externalContextId?.length > 0
            ? await coursesService.search(externalContextId, discipline, intl.locale, maxResults, sectors)
            : {
                records: {},
                state: {
                  totalEllapsed: 0,
                  totalFound: 0
                },
                dimensionFound: {}
              };
        const activitiesObj = dataStructure.convertArrayToObject(allActivities, 'externalId');

        const myCoursesArray = Object.values(searchResults.records)
          .reduce((reduced, records) => {
            records.forEach(course => {
              const activity = activitiesObj[course.externalContextId];
              course.additionalInfo = activity?.additionalInfo;

              // Add learningActivities with matching contentReference to additionalInfo
              const matchingLearningActivities = (learningActivities || []).filter(la => la.contentReference === course.externalContextId);
              if (matchingLearningActivities.length > 0) {
                if (!course.learningAdditionalInfo) course.learningAdditionalInfo = [];
                course.learningAdditionalInfo.push(...matchingLearningActivities.map(la => ({ id: la.courseId, name: la.name })));
              }
            });
            return [...reduced, ...records];
          }, [])
          .filter(
            (course, index, coursesArray) =>
              coursesArray.findLastIndex(instance => {
                return instance.externalContextId === course.externalContextId;
              }) === index
          )
          .sort((a, b) => (a.title < b.title ? -1 : 1));

        setMyCourses(myCoursesArray);
        setIsLoading(false);
      } catch (error) {
        setMyCourses([]);
        setIsErrored(true);
        setIsLoading(false);
      }
    },
    [discipline, intl.locale, maxResults, sectors, studentId]
  );

  useEffect(() => {
    if (studentId && !myCourses) {
      if (!activities || !learningActivities) {
        setIsLoading(true);
        Promise.all([activitiesService.loadActivities(studentId, 'COURSE', schoolId), activitiesService.loadLearningActivities(studentId)])
          .then(async ([courseActivities, learningActs]) => {
            dispatch(setActivities({ type: 'courses', activities: courseActivities }));
            dispatch(setActivities({ type: 'learningActivities', activities: learningActs }));
            setMyCoursesByActivities(courseActivities, learningActs);
          })
          .catch(error => {
            dispatch(setActivities({ type: 'courses', activities: [] }));
            dispatch(setActivities({ type: 'learningActivities', activities: [] }));
            setMyCourses([]);
            console.error(error);
            setIsLoading(false);
            setIsErrored(true);
          });
      } else {
        setMyCoursesByActivities(activities, learningActivities);
      }
    }
  }, [activities, learningActivities, dispatch, myCourses, schoolId, setMyCoursesByActivities, studentId]);

  return { isLoading, isErrored, myCourses };
};
